import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';

import NamedIcon from '@v2/shared/components/NamedIcon';
import { SaleCategory } from '@v2/shared/lib/megaSale';
import FullScreenDialog from '@v2/shared/components/FullScreenDialog';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  categories: SaleCategory[];
  onSelect: (categoryId: number) => void;
};

/**
 * What the hamburger opens on a sale page.
 *
 * On every other page it is the site's category tree. Here it is *this sale's* sections
 * — the same list the desktop rail shows down the side, which desktop has room for and a
 * phone does not. Choosing one scrolls the grid rather than navigating: there is one
 * page and the categories are places within it.
 *
 * It reuses the shared FullScreenDialog, so the panel, the animation and the close button
 * are the ones DiscountMags' mobile navigation already uses. That is also the one thing
 * the sale header keeps on every brand: the other three brands hide their *site*
 * categories behind a Disclosure that unfolds in the header, which is right for a list
 * you leave the page by and wrong for a list of places within this one — a panel you can
 * run down and close is what a sale's sections want at any width.
 */
export default function SaleCategoriesDialog({ isOpen, toggle, close, categories, onSelect }: Props) {
  return (
    <FullScreenDialog isOpen={isOpen} toggle={toggle} close={close} title="Categories" slideInFrom="left">
      <nav aria-label="Sale categories">
        <ul className="list-none m-0 p-0 pb-8">
          {categories.map((category) => (
            <li key={category.id}>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-base font-medium text-slate-800 hover:bg-slate-50 active:bg-slate-100 border-b border-slate-100"
                onClick={() => {
                  onSelect(category.id);
                  close();
                }}
              >
                <span className="flex items-center gap-3 min-w-0">
                  {category.icon && <NamedIcon name={category.icon} className="text-slate-400 w-5 shrink-0" />}
                  <span className="truncate">{category.name}</span>
                </span>
                <FontAwesomeIcon icon={faAngleRight} className="text-slate-300 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </FullScreenDialog>
  );
}
